import React from "react";
import { useNavigate } from "react-router-dom";
import { Image, Stack } from "react-bootstrap";

import "./LocationOutOfBounds.scss";
import PrimaryActionButton from "../Buttons/PrimaryActionButton/PrimaryActionButton";
import { ROUTES } from "../../utils/constants";
import ImageUsaMap from "../../images/usa-map.png";
import ImageNoLocation from "../../images/no-location.png";
import BottomDrawer from "../BottomDrawer/BottomDrawer";
import { IpInfoCountry } from "../../types";
import IneligibleLocations from "../IneligibleLocations/IneligibleLocations";
import useDisclosure from "../../hooks/useDisclosure";
import { GenericModalProps } from "../../utils/constants/generic-types";
import useAuth from "../../hooks/useAuth";

interface LocationOutOfBoundsProps extends GenericModalProps {
  countryCode?: string;
}

enum IMAGE_LOOKUP_KEYS {
  US = "us",
  OTHERS = "others",
}

const imageParams = {
  [IMAGE_LOOKUP_KEYS.US]: {
    src: ImageUsaMap,
    alt: "USA Map of eligible and ineligible locations",
    width: 343,
    height: 209,
  },
  [IMAGE_LOOKUP_KEYS.OTHERS]: {
    src: ImageNoLocation,
    alt: "Location disallowed",
    width: 120,
    height: 120,
  },
};

export default function LocationOutOfBounds({
  isOpen,
  onClose,
  countryCode,
}: LocationOutOfBoundsProps) {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const userFirstName = userProfile?.firstName;
  const imageLookupKey =
    countryCode === IpInfoCountry.US
      ? IMAGE_LOOKUP_KEYS.US
      : IMAGE_LOOKUP_KEYS.OTHERS;

  const {
    isOpen: isIneligibleLocationsOpen,
    onOpen: openIneligibleLocations,
    onClose: closeIneligibleLocations,
  } = useDisclosure();

  const seeIneligibleLocations = () => {
    onClose();
    openIneligibleLocations();
  };

  return (
    <>
      <BottomDrawer
        isOpen={isOpen}
        onClose={onClose}
        containerClass="LocationOutOfBoundsModal"
      >
        <div className="pt-4 d-flex flex-column justify-content-between ModalContent">
          <Stack
            className={`align-items-center ${
              countryCode === IpInfoCountry.US
                ? "TextWithUsaMap"
                : "TextWithLocationIcon"
            }`}
          >
            <Stack className="align-items-start text-start Copy">
              <h1 className="Grotesk MainCopy">
                Oops, Hyper does not operate in your location.
              </h1>
              <div>
                <p className="Grotesk SubCopy">
                  Hey {userFirstName}, unfortunately Hyper does not operate in
                  your current location.{" "}
                  {countryCode === IpInfoCountry.US &&
                    "We currently operate in 33 states and counting."}
                </p>
                {countryCode !== IpInfoCountry.US && (
                  <button
                    className="p-0 Grotesk SeeIneligibleLocations"
                    onClick={seeIneligibleLocations}
                  >
                    See ineligible locations
                  </button>
                )}
              </div>
            </Stack>
            <div>
              <Image
                src={imageParams[imageLookupKey].src}
                alt={imageParams[imageLookupKey].alt}
                width={imageParams[imageLookupKey].width}
                height={imageParams[imageLookupKey].height}
                className="PictorialImage"
              />
            </div>
          </Stack>
          <PrimaryActionButton
            text="Go Home"
            onClick={() => navigate(ROUTES.GAMES.ROOT)}
          />
        </div>
      </BottomDrawer>

      {isIneligibleLocationsOpen && (
        <IneligibleLocations
          isOpen={isIneligibleLocationsOpen}
          onClose={closeIneligibleLocations}
        />
      )}
    </>
  );
}
